import type { ReactNode } from 'react';
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ALL_PROJECTS } from '../graphql/GET_ALL_PROJECTS';
import { renderContents } from './common';
import { ProjectListContext, PAGE_LIMIT } from './projectListContext';
import { validVideoUrlRegex } from '../services/videoUrlParser';

interface FilterOption {
  id: string;
  name: string;
}

interface FilteredProjectListContextProviderProps {
  children: ReactNode;
  searchValue: string;
  selectedFields: FilterOption[];
  selectedLocations: FilterOption[];
  selectedUserId?: string | null;
  activeFilter: string;
}

const INVALID_VIDEO_PLACEHOLDERS = ['null', 'undefined', 'n/a', 'none'];

const hasValidVideoUrl = (videoUrl: string | null | undefined): boolean => {
  const trimmedUrl = videoUrl?.trim();
  if (!trimmedUrl) return false;
  if (INVALID_VIDEO_PLACEHOLDERS.includes(trimmedUrl.toLowerCase())) return false;
  return validVideoUrlRegex.test(trimmedUrl);
};

export const FilteredProjectListContextProvider: React.FC<
  FilteredProjectListContextProviderProps
> = ({ children, searchValue, selectedFields, selectedLocations, selectedUserId, activeFilter }) => {
  const hasActiveFilters =
    !!searchValue.trim() ||
    !!selectedUserId ||
    selectedFields.length > 0 ||
    selectedLocations.length > 0 ||
    activeFilter !== 'all';

  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isExhausted, setIsExhausted] = useState(false);
  const prefetchAbortRef = useRef(false);
  const isPrefetchingRef = useRef(false);
  const filterPrefetchStartedRef = useRef(false);

  const { loading, error, data, fetchMore } = useQuery(GET_ALL_PROJECTS, {
    variables: { limit: PAGE_LIMIT, page: 1 },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first'
  });

  const cachedProjects = data?.getAllProjects?.projects || [];
  const totalCount = data?.getAllProjects?.totalCount || 0;
  const allProjectsLoaded =
    isExhausted || (totalCount > 0 && cachedProjects.length >= totalCount);

  const mergeProjects = useCallback((prev: any, fetchMoreResult: any) => {
    if (!fetchMoreResult?.getAllProjects) {
      return prev;
    }

    const incoming = fetchMoreResult.getAllProjects.projects || [];
    const existing = prev.getAllProjects?.projects || [];
    const seen = new Set(existing.map((project: { id: string }) => project.id));
    const uniqueIncoming = incoming.filter(
      (project: { id: string }) => !seen.has(project.id)
    );
    const projects = [...existing, ...uniqueIncoming];
    const reachedEnd = incoming.length === 0 || incoming.length < PAGE_LIMIT;

    if (reachedEnd) {
      setIsExhausted(true);
    }

    return {
      ...prev,
      getAllProjects: {
        ...prev.getAllProjects,
        ...fetchMoreResult.getAllProjects,
        projects,
        totalCount: reachedEnd
          ? projects.length
          : fetchMoreResult.getAllProjects.totalCount ??
            prev.getAllProjects.totalCount,
        page: fetchMoreResult.getAllProjects.page
      }
    };
  }, []);

  const loadNextPage = useCallback(async () => {
    if (isExhausted) {
      return { exhausted: true, incomingCount: 0 };
    }

    const currentPage = Math.floor(cachedProjects.length / PAGE_LIMIT) + 1;
    const result = await fetchMore({
      variables: {
        limit: PAGE_LIMIT,
        page: currentPage
      },
      updateQuery: (prev, { fetchMoreResult }) =>
        mergeProjects(prev, fetchMoreResult)
    });

    const incoming = result?.data?.getAllProjects?.projects || [];
    const exhausted = incoming.length === 0 || incoming.length < PAGE_LIMIT;
    if (exhausted) {
      setIsExhausted(true);
    }

    return { exhausted, incomingCount: incoming.length };
  }, [cachedProjects.length, fetchMore, isExhausted, mergeProjects]);

  // Only prefetch when filters are applied (client-side filtering needs more pages).
  // Never auto-prefetch on feed load — that races comments and overloads the API.
  useEffect(() => {
    if (!hasActiveFilters) {
      prefetchAbortRef.current = true;
      filterPrefetchStartedRef.current = false;
      return;
    }

    if (
      !data ||
      allProjectsLoaded ||
      isPrefetchingRef.current ||
      filterPrefetchStartedRef.current
    ) {
      return;
    }

    filterPrefetchStartedRef.current = true;
    prefetchAbortRef.current = false;
    isPrefetchingRef.current = true;
    setIsPrefetching(true);

    const prefetchForFilters = async () => {
      try {
        while (!prefetchAbortRef.current) {
          const { exhausted } = await loadNextPage();
          if (exhausted) {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (err) {
        console.error('Filter prefetch error:', err);
        setIsExhausted(true);
      } finally {
        isPrefetchingRef.current = false;
        setIsPrefetching(false);
      }
    };

    void prefetchForFilters();

    return () => {
      prefetchAbortRef.current = true;
    };
  }, [hasActiveFilters, data, allProjectsLoaded, loadNextPage]);

  const onFetchMore = useCallback(async () => {
    if (hasActiveFilters || allProjectsLoaded || isPrefetching || isExhausted) {
      return;
    }

    try {
      await loadNextPage();
    } catch (err) {
      console.error('Manual fetch error:', err);
      setIsExhausted(true);
    }
  }, [hasActiveFilters, allProjectsLoaded, isPrefetching, isExhausted, loadNextPage]);

  const filteredProjects = useMemo(() => {
    if (!hasActiveFilters) {
      return cachedProjects;
    }

    let filtered = [...cachedProjects];

    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.title?.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.author?.profile?.name?.toLowerCase().includes(searchLower)
      );
    }

    if (selectedUserId) {
      filtered = filtered.filter(
        (project) => project.author?.id === selectedUserId
      );
    }

    if (selectedFields.length > 0) {
      filtered = filtered.filter((project) => {
        const hasMatchingGenre = project.genres?.some((genre) =>
          selectedFields.some((field) => field.id === genre.id)
        );

        const hasMatchingProfession = project.collaborations?.some(
          (collab: any) =>
            collab.requiredProfessions?.some((prof: any) =>
              selectedFields.some((field) => field.id === prof.id)
            )
        );

        return hasMatchingGenre || hasMatchingProfession;
      });
    }

    if (selectedLocations.length > 0) {
      filtered = filtered.filter((project) =>
        project.locations?.some((location: any) =>
          selectedLocations.some((selected) => selected.id === location.id)
        )
      );
    }

    if (activeFilter === 'videos') {
      filtered = filtered.filter((project) => hasValidVideoUrl(project.videoUrl));
    } else if (activeFilter === 'projects') {
      filtered = filtered.filter(
        (project) => !hasValidVideoUrl(project.videoUrl)
      );
    }
    return filtered;
  }, [
    cachedProjects,
    searchValue,
    selectedFields,
    selectedLocations,
    selectedUserId,
    hasActiveFilters,
    activeFilter
  ]);

  const resolvedTotalCount = hasActiveFilters
    ? isPrefetching || !allProjectsLoaded
      ? Math.max(filteredProjects.length + 1, filteredProjects.length)
      : filteredProjects.length
    : allProjectsLoaded
      ? cachedProjects.length
      : totalCount;

  return (
    <ProjectListContext.Provider
      value={{
        error,
        onFetchMore,
        isLoading: loading || (hasActiveFilters && isPrefetching),
        projects: filteredProjects,
        page: data?.getAllProjects?.page || 1,
        totalCount: resolvedTotalCount
      }}
    >
      {renderContents({
        children,
        data,
        error,
        loading
      })}
    </ProjectListContext.Provider>
  );
};
